/**
 * Floating Tooltip Web Component
 *
 * Handles positioning and visibility for tooltips that need to escape overflow:hidden
 * containers. Uses fixed positioning with viewport-relative coordinates.
 *
 * Responsibilities:
 * - Show/hide tooltip content on hover, focus, and keyboard events
 * - Calculate and apply fixed positioning based on trigger location
 * - Clamp position to keep tooltip within viewport bounds
 * - Hide on scroll to prevent stale positioning
 * - Manage aria-hidden for screen reader accessibility
 *
 * Expects two children with classes: .floating-tooltip-trigger and .floating-tooltip-content
 * Reads position from the "position" attribute: "above" (default), "below", "left", "right"
 *
 * With the "show-if-truncated" attribute the tooltip only shows while its label
 * is actually clipped, and the trigger is a tab stop only while that holds. The
 * measured element is .floating-tooltip-truncation-target, which the caller must
 * mark: without it the measurement falls back to .floating-tooltip-trigger,
 * which is width-fit and so never overflows.
 *
 * See Components.FloatingTooltip for the Elm API.
 */

const TRIGGER_GAP = 8;
const VIEWPORT_PADDING = 8;

class FloatingTooltip extends HTMLElement {
  constructor() {
    super();
    this._isVisible = false;
    this._showTimer = null;
    this._resizeObserver = null;
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onScroll = this._onScroll.bind(this);
  }

  static get observedAttributes() {
    return ["position"];
  }

  get position() {
    return this.getAttribute("position") || "above";
  }

  // Hover delay (ms) before the tooltip appears. Defaults to 0 (immediate) so
  // existing tooltips are unaffected.
  get delay() {
    const parsed = parseInt(this.getAttribute("delay"), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  get showIfTruncated() {
    return this.hasAttribute("show-if-truncated");
  }

  connectedCallback() {
    this._trigger = this.querySelector(".floating-tooltip-trigger");
    this._content = this.querySelector(".floating-tooltip-content");

    if (this._trigger && this._content) {
      // Set initial styles on content
      this._content.style.position = "fixed";
      this._content.style.zIndex = "9999";
      this._content.style.visibility = "hidden";
      this._content.style.pointerEvents = "none";

      // For screen readers: content is always accessible via aria-describedby
      // but we use aria-hidden to indicate visual state
      this._content.setAttribute("aria-hidden", "true");

      // Add event listeners
      this._trigger.addEventListener("mouseenter", this._onMouseEnter);
      this._trigger.addEventListener("mouseleave", this._onMouseLeave);
      // Focus shows immediately (keyboard a11y); only pointer hover is delayed.
      this._trigger.addEventListener("focus", this._onFocus);
      this._trigger.addEventListener("blur", this._onBlur);
      this._trigger.addEventListener("keydown", this._onKeyDown);

      if (this.showIfTruncated) {
        // Measuring here would force a synchronous layout per tooltip.
        this._observeTruncation();
      }
    }
  }

  disconnectedCallback() {
    if (this._trigger) {
      this._trigger.removeEventListener("mouseenter", this._onMouseEnter);
      this._trigger.removeEventListener("mouseleave", this._onMouseLeave);
      this._trigger.removeEventListener("focus", this._onFocus);
      this._trigger.removeEventListener("blur", this._onBlur);
      this._trigger.removeEventListener("keydown", this._onKeyDown);
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    this._clearShowTimer();
    this._removeGlobalListeners();
  }

  _onMouseEnter() {
    const delay = this.delay;
    if (delay > 0) {
      this._clearShowTimer();
      this._showTimer = setTimeout(() => {
        this._showTimer = null;
        this._show();
      }, delay);
    } else {
      this._show();
    }
  }

  _onFocus() {
    this._show();
  }

  _onMouseLeave() {
    this._clearShowTimer();
    this._hide();
  }

  _onBlur() {
    this._onMouseLeave();

    // A resize may have un-clipped the label while it held focus, in which case
    // _syncTabStop deferred removing the tab stop rather than pull tabindex off
    // the focused element. Now that focus has left, settle it.
    if (this.showIfTruncated) this._syncTabStop();
  }

  _clearShowTimer() {
    if (this._showTimer) {
      clearTimeout(this._showTimer);
      this._showTimer = null;
    }
  }

  _onKeyDown(e) {
    if (e.key === "Escape" && this._isVisible) {
      this._hide();
    }
  }

  _truncationTarget() {
    return this.querySelector(".floating-tooltip-truncation-target") || this._trigger;
  }

  _isTruncated() {
    const target = this._truncationTarget();

    if (!target) return false;

    // scrollWidth and clientWidth are both rounded integers, so any positive
    // delta is real overflow — the narrowest genuine clip reports exactly 1.
    return target.scrollWidth - target.clientWidth >= 1;
  }

  // Focus is only worth a tab stop when there is a tooltip to reveal, so
  // truncation-gated triggers are focusable only while their label is clipped.
  // Otherwise every badge in a row becomes an inert stop. Whether a label clips
  // depends on viewport width and locale, hence the ResizeObserver below.
  _syncTabStop() {
    if (!this._trigger) return;

    if (this._isTruncated()) {
      this._trigger.setAttribute("tabindex", "0");
      return;
    }

    // Removing tabindex from the focused element makes the browser drop focus to
    // <body>, so leave it in place and let _onBlur clear it once focus moves on.
    if (document.activeElement !== this._trigger) {
      this._trigger.removeAttribute("tabindex");
    }

    if (this._isVisible) this._hide();
  }

  _observeTruncation() {
    const target = this._truncationTarget();

    if (!target) return;

    // Without ResizeObserver there is no initial callback, so sync once here.
    if (typeof ResizeObserver === "undefined") {
      this._syncTabStop();
      return;
    }

    // connectedCallback can run more than once for the same element, so drop any
    // prior observer rather than leaving it running and unreachable.
    if (this._resizeObserver) this._resizeObserver.disconnect();

    // The observer's first callback, after layout, sets the initial tab stop. It
    // is skipped for 0x0 / display:none targets, which can't be clipped anyway.
    this._resizeObserver = new ResizeObserver(() => this._syncTabStop());
    this._resizeObserver.observe(target);
  }

  _getArrowElement() {
    return this._content?.querySelector(".floating-tooltip-arrow") ?? null;
  }

  _measureContentRect() {
    // Pin at 0,0 so getBoundingClientRect reveals the containing-block origin.
    // When position:fixed is relative to the viewport, rect.left ≈ 0.
    // When a transformed ancestor exists, rect.left/top give its offset.
    this._content.style.left = "0px";
    this._content.style.top = "0px";
    return this._content.getBoundingClientRect();
  }

  _positionAbove(triggerRect, contentRect) {
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const arrow = this._getArrowElement();

    if (arrow) {
      const arrowRect = arrow.getBoundingClientRect();
      const arrowCenterX = arrowRect.left - contentRect.left + arrowRect.width / 2;
      const arrowBottom = arrowRect.bottom - contentRect.top;

      return {
        left: triggerCenterX - arrowCenterX,
        top: triggerRect.top - arrowBottom - TRIGGER_GAP,
        preserveHorizontalAlignment: true,
      };
    }

    return {
      left: triggerCenterX - contentRect.width / 2,
      top: triggerRect.top - contentRect.height - TRIGGER_GAP,
      preserveHorizontalAlignment: false,
    };
  }

  _show() {
    if (!this._content || !this._trigger) return;
    if (this.showIfTruncated && !this._isTruncated()) return;

    // Anchor to the actual trigger content (the wrapped element) rather than the
    // wrapper div, which may stretch full-width and would otherwise center the
    // tooltip on the whole row instead of the target.
    const triggerTarget = this._trigger.firstElementChild || this._trigger;
    const triggerRect = triggerTarget.getBoundingClientRect();
    const contentRect = this._measureContentRect();
    const isRtl = getComputedStyle(this).direction === "rtl";

    // `position: fixed` is relative to the nearest ancestor with a transform /
    // filter / perspective (e.g. the wizard modal), not always the viewport. With
    // the content pinned at left:0/top:0 above, its measured viewport position
    // tells us where that containing-block origin sits, so we can convert our
    // viewport-space coordinates back into the content's local space.
    const originLeft = contentRect.left;
    const originTop = contentRect.top;

    // In RTL mode, swap "left" and "right" positions
    let position = this.position;
    if (isRtl) {
      if (position === "left") position = "right";
      else if (position === "right") position = "left";
    }

    let left, top;
    let preserveHorizontalAlignment = false;

    switch (position) {
      case "below":
        left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
        top = triggerRect.bottom + TRIGGER_GAP;
        break;
      case "left":
        left = triggerRect.left - contentRect.width - TRIGGER_GAP;
        top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
        break;
      case "right":
        left = triggerRect.right + TRIGGER_GAP;
        top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
        break;
      case "above":
      default: {
        const abovePosition = this._positionAbove(triggerRect, contentRect);
        left = abovePosition.left;
        top = abovePosition.top;
        preserveHorizontalAlignment = abovePosition.preserveHorizontalAlignment;
        break;
      }
    }

    // Keep within viewport horizontally unless arrow alignment must be preserved
    if (!preserveHorizontalAlignment) {
      if (left < VIEWPORT_PADDING) {
        left = VIEWPORT_PADDING;
      } else if (left + contentRect.width > window.innerWidth - VIEWPORT_PADDING) {
        left = window.innerWidth - contentRect.width - VIEWPORT_PADDING;
      }
    }

    // Keep within viewport vertically
    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING;
    } else if (top + contentRect.height > window.innerHeight - VIEWPORT_PADDING) {
      top = window.innerHeight - contentRect.height - VIEWPORT_PADDING;
    }

    // Convert the viewport-space position into the content's containing-block
    // space (a no-op when that block is the viewport, i.e. origin is 0,0).
    this._content.style.left = `${left - originLeft}px`;
    this._content.style.top = `${top - originTop}px`;
    this._content.style.visibility = "visible";
    this._content.setAttribute("aria-hidden", "false");

    this._isVisible = true;

    // Close on scroll
    document.addEventListener("scroll", this._onScroll, true);
  }

  _hide() {
    this._clearShowTimer();

    if (!this._content) return;

    this._content.style.visibility = "hidden";
    this._content.setAttribute("aria-hidden", "true");
    this._isVisible = false;

    this._removeGlobalListeners();
  }

  _onScroll() {
    this._hide();
  }

  _removeGlobalListeners() {
    document.removeEventListener("scroll", this._onScroll, true);
  }
}

const init = () => {
  if (!customElements.get("floating-tooltip")) {
    customElements.define("floating-tooltip", FloatingTooltip);
  }
};

export default { init };
